import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Activity, User, Calendar, History, LogOut, Sparkles, Phone, Briefcase, Heart as HeartIcon, MessageCircle, Grid3x3, Home, Stars, Lock, CalendarDays } from 'lucide-react';
import AIChat from '../components/AIChat';
import QuickUpgrade from '../components/QuickUpgrade';

function Dashboard({ onLogout }) {
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeModule, setActiveModule] = useState('numerology');
  const [subscriptionAccess, setSubscriptionAccess] = useState({
    features: {},
    loading: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, profileRes, accessRes] = await Promise.all([
        axios.get('/api/auth/me'),
        axios.get('/api/profile'),
        axios.get('/api/subscriptions/access')
      ]);
      setUser(userRes.data);
      if (profileRes.data.exists) {
        setProfile(profileRes.data.profile);
      }
      setSubscriptionAccess({
        features: accessRes.data.features,
        activePlans: accessRes.data.active_plans,
        hasBundle: accessRes.data.has_bundle,
        loading: false
      });
    } catch (err) {
      console.error('Error fetching data:', err);
      setSubscriptionAccess({ features: {}, loading: false });
    } finally {
      setLoading(false);
    }
  };

  const hasAccess = (feature) => {
    return subscriptionAccess.features[feature] || false;
  };

  const services = [
    {
      title: 'Full Analysis',
      description: 'Complete numerology reading with Loshu Grid',
      icon: Sparkles,
      path: '/analysis',
      color: '#00D9FF'
    },
    {
      title: 'Name Correction',
      description: 'Get name suggestions for desired energy',
      icon: User,
      path: '/name-correction',
      color: '#7C3AED'
    },
    {
      title: 'Mobile Number',
      description: 'Score your mobile number',
      icon: Phone,
      path: '/mobile-score',
      color: '#F59E0B'
    },
    {
      title: 'Business Name',
      description: 'Analyze business name alignment',
      icon: Briefcase,
      path: '/business-name',
      color: '#10B981'
    },
    {
      title: 'Love Compatibility',
      description: 'Check relationship compatibility',
      icon: HeartIcon,
      path: '/love-compatibility',
      color: '#EC4899'
    },
    {
      title: 'Energy Tracker',
      description: 'Track daily energy and remedies',
      icon: Activity,
      path: '/energy-tracker',
      color: '#FB923C'
    },
    {
      title: 'History',
      description: 'View past readings',
      icon: History,
      path: '/history',
      color: '#9CA3AF'
    }
  ];

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        {/* Header with Logout */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <motion.h1
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            style={{ fontSize: '2.5rem', fontWeight: 700 }}
          >
            Astrowins
          </motion.h1>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button
              onClick={() => navigate('/profile')}
              className="btn btn-ghost"
              data-testid="profile-button"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <User size={20} />
              Profile
            </button>
            <button
              onClick={() => setChatOpen(true)}
              className="btn btn-primary"
              data-testid="open-chat"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <MessageCircle size={20} />
              Ask AI
            </button>
            <button
              onClick={onLogout}
              className="btn btn-ghost"
              data-testid="logout-button"
            >
              <LogOut size={20} style={{ marginRight: '0.5rem' }} />
              Logout
            </button>
          </div>
        </div>

        {/* Main Navigation Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '0', 
          marginBottom: '2rem',
          background: '#151827',
          borderRadius: '16px',
          padding: '0.5rem',
          border: '2px solid #2D3748'
        }}>
          <button
            onClick={() => {
              if (hasAccess('numerology')) {
                setActiveModule('numerology');
              } else {
                navigate('/');
                setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100);
              }
            }}
            className={activeModule === 'numerology' ? 'btn btn-primary' : 'btn btn-ghost'}
            data-testid="tab-numerology"
            style={{
              flex: 1,
              padding: window.innerWidth < 640 ? '0.75rem 0.5rem' : '1rem',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: window.innerWidth < 640 ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: window.innerWidth < 640 ? '0.25rem' : '0.5rem',
              fontSize: window.innerWidth < 640 ? '0.75rem' : '1.125rem',
              fontWeight: 600,
              opacity: hasAccess('numerology') ? 1 : 0.5
            }}
          >
            <Grid3x3 size={window.innerWidth < 640 ? 18 : 24} />
            <span style={{ whiteSpace: window.innerWidth < 640 ? 'normal' : 'nowrap' }}>
              {window.innerWidth < 640 ? 'NUMERO' : 'NUMEROLOGY'}
            </span>
            {!hasAccess('numerology') && <Lock size={14} style={{ marginLeft: '0.25rem' }} />}
          </button>
          <button
            onClick={() => {
              if (hasAccess('vastu')) {
                setActiveModule('vastu');
              } else {
                navigate('/');
                setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100);
              }
            }}
            className={activeModule === 'vastu' ? 'btn btn-primary' : 'btn btn-ghost'}
            data-testid="tab-vastu"
            style={{
              flex: 1,
              padding: window.innerWidth < 640 ? '0.75rem 0.5rem' : '1rem',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: window.innerWidth < 640 ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: window.innerWidth < 640 ? '0.25rem' : '0.5rem',
              fontSize: window.innerWidth < 640 ? '0.75rem' : '1.125rem',
              fontWeight: 600,
              opacity: hasAccess('vastu') ? 1 : 0.5
            }}
          >
            <Home size={window.innerWidth < 640 ? 18 : 24} />
            VASTU
            {!hasAccess('vastu') && <Lock size={14} style={{ marginLeft: '0.25rem' }} />}
          </button>
          <button
            onClick={() => {
              if (hasAccess('astrology')) {
                setActiveModule('astrology');
              } else {
                navigate('/');
                setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100);
              }
            }}
            className={activeModule === 'astrology' ? 'btn btn-primary' : 'btn btn-ghost'}
            data-testid="tab-astrology"
            style={{
              flex: 1,
              padding: window.innerWidth < 640 ? '0.75rem 0.5rem' : '1rem',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: window.innerWidth < 640 ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: window.innerWidth < 640 ? '0.25rem' : '0.5rem',
              fontSize: window.innerWidth < 640 ? '0.75rem' : '1.125rem',
              fontWeight: 600,
              opacity: hasAccess('astrology') ? 1 : 0.5
            }}
          >
            <Stars size={window.innerWidth < 640 ? 18 : 24} />
            <span style={{ whiteSpace: window.innerWidth < 640 ? 'normal' : 'nowrap' }}>
              {window.innerWidth < 640 ? 'ASTRO' : 'ASTROLOGY'}
            </span>
            {!hasAccess('astrology') && <Lock size={14} style={{ marginLeft: '0.25rem' }} />}
          </button>
          <button
            onClick={() => {
              if (hasAccess('weekly_report')) {
                setActiveModule('weekly_forecast');
              } else {
                navigate('/');
                setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100);
              }
            }}
            className={activeModule === 'weekly_forecast' ? 'btn btn-primary' : 'btn btn-ghost'}
            data-testid="tab-weekly-forecast"
            style={{
              flex: 1,
              padding: window.innerWidth < 640 ? '0.75rem 0.5rem' : '1rem',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: window.innerWidth < 640 ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: window.innerWidth < 640 ? '0.25rem' : '0.5rem',
              fontSize: window.innerWidth < 640 ? '0.75rem' : '1.125rem',
              fontWeight: 600,
              opacity: hasAccess('weekly_report') ? 1 : 0.5
            }}
          >
            <CalendarDays size={window.innerWidth < 640 ? 18 : 24} />
            <span style={{ whiteSpace: window.innerWidth < 640 ? 'normal' : 'nowrap' }}>
              {window.innerWidth < 640 ? 'WEEKLY' : 'WEEKLY FORECAST'}
            </span>
            {!hasAccess('weekly_report') && <Lock size={14} style={{ marginLeft: '0.25rem' }} />}
          </button>
        </div>

        {user && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card"
            style={{ marginBottom: '2rem' }}
          >
            <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
              Welcome, {user.full_name || user.email}!
            </h2>
            {!profile ? (
              <div>
                <p style={{ color: '#9CA3AF', marginBottom: '1rem' }}>
                  Complete your profile to get personalized insights.
                </p>
                <button
                  onClick={() => navigate('/data-input')}
                  className="btn btn-primary"
                  data-testid="complete-profile"
                >
                  Complete Profile
                </button>
              </div>
            ) : (
              <div>
                <p style={{ color: '#10B981', marginBottom: '0.5rem' }}>
                  ✓ Profile complete. Ready for readings!
                </p>
                {profile.edit_count !== undefined && (
                  <p style={{ color: '#9CA3AF', fontSize: '0.875rem' }}>
                    Profile edits used: {profile.edit_count}/3 • {3 - profile.edit_count} edits remaining
                  </p>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Module Content */}
        {activeModule === 'numerology' && (
          <div>
            {hasAccess('numerology') ? (
              <>
                <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem' }}>Numerology Services</h2>

                <div className="card-grid">
                  {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="card"
                  onClick={() => navigate(service.path)}
                  style={{ cursor: 'pointer' }}
                  data-testid={`service-${service.title.toLowerCase().replace(/ /g, '-')}`}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: `rgba(${service.color === '#00D9FF' ? '0, 217, 255' : '124, 58, 237'}, 0.1)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '1rem'
                    }}>
                      <service.icon size={24} color={service.color} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>
                        {service.title}
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>
                        {service.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
                </div>
              </>
            ) : (
              <QuickUpgrade currentPlan="numerology" user={user} />
            )}
          </div>
        )}

        {/* Vastu Module */}
        {activeModule === 'vastu' && (
          <div>
            {hasAccess('vastu') ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card"
                style={{ textAlign: 'center', padding: '3rem', marginBottom: '2rem' }}
              >
                <Home size={64} color="#10B981" style={{ margin: '0 auto 1.5rem' }} />
                <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Vastu Analysis</h2>
                <p style={{ color: '#9CA3AF', fontSize: '1.125rem', marginBottom: '2rem', maxWidth: '700px', margin: '0 auto 2rem' }}>
                  Analyze your house for directional doshas and get 5-layer remedies: Immediate colors/objects, Pyramids/Plants, Feng Shui cures, Construction advice, and Daily practices.
                </p>
                <button
                  onClick={() => navigate('/vastu')}
                  className="btn btn-primary"
                  data-testid="start-vastu-analysis"
                  style={{ fontSize: '1.125rem', padding: '1rem 2rem' }}
                >
                  Start Vastu Analysis
                </button>
              </motion.div>
            ) : (
              <QuickUpgrade currentPlan="vastu" user={user} />
            )}
          </div>
        )}

        {/* Astrology Module */}
        {activeModule === 'astrology' && (
          <div>
            {hasAccess('astrology') ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card"
                style={{ textAlign: 'center', padding: '3rem', marginBottom: '2rem' }}
              >
                <Stars size={64} color="#7C3AED" style={{ margin: '0 auto 1.5rem' }} />
                <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Vedic Astrology Analysis</h2>
                <p style={{ color: '#9CA3AF', fontSize: '1.125rem', marginBottom: '2rem', maxWidth: '700px', margin: '0 auto 2rem' }}>
                  Get your complete Kundali with 12 Rashis, 9 planets, 27 Nakshatras, current Dasha period, live planetary transits, 2-month advance predictions, and lunar astrology for past life & karmic analysis.
                </p>
                <button
                  onClick={() => navigate('/astrology')}
                  className="btn btn-primary"
                  data-testid="view-kundali"
                  style={{ fontSize: '1.125rem', padding: '1rem 2rem' }}
                >
                  View Your Kundali
                </button>
              </motion.div>
            ) : (
              <QuickUpgrade currentPlan="astrology" user={user} />
            )}
          </div>
        )}

        {/* Weekly Forecast Module */}
        {activeModule === 'weekly_forecast' && (
          <div>
            {hasAccess('weekly_report') ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card"
                style={{ textAlign: 'center', padding: '3rem', marginBottom: '2rem' }}
              >
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #00D9FF 0%, #7C3AED 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  <Sparkles size={40} color="#FFF" />
                </div>
                <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Your Weekly Cosmic Forecast</h2>
                <p style={{ color: '#9CA3AF', fontSize: '1.125rem', marginBottom: '2rem', maxWidth: '700px', margin: '0 auto 2rem' }}>
                  AI-powered personalized predictions for Money, Love, Career Opportunities & Risk alerts. 
                  Get precise day-by-day guidance based on your unique numerological and astrological profile.
                </p>
                <button
                  onClick={() => navigate('/weekly-insights')}
                  className="btn btn-primary"
                  data-testid="view-weekly-forecast"
                  style={{ fontSize: '1.125rem', padding: '1rem 2rem' }}
                >
                  View This Week&apos;s Forecast
                </button>
              </motion.div>
            ) : (
              <QuickUpgrade currentPlan="weekly_report" user={user} />
            )}
          </div>
        )}
      </div>

      {/* AI Chat Modal */}
      <AIChat isOpen={chatOpen} onClose={() => setChatOpen(false)} />
    </div>
  );
}

export default Dashboard;